import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Chip,
  Autocomplete,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { locationsApi } from '../../api/services';
import type { Location, Mesh } from '../../api/config';
import { extractApiError } from '../../utils/apiErrors';

interface MeshMembersDialogProps {
  open: boolean;
  onClose: () => void;
  mesh: Mesh;
}

// Manage a mesh's member locations directly from the meshes table (mirrors
// product-equivalencies' MembershipManager). Membership writes go through the
// existing PUT /locations/:id (mesh_id), so the backend's mesh-org validation
// applies unchanged.
export const MeshMembersDialog: React.FC<MeshMembersDialogProps> = ({
  open,
  onClose,
  mesh,
}) => {
  const queryClient = useQueryClient();
  const [selectedLocation, setSelectedLocation] = React.useState<Location | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // One org-scoped query serves both lists: members (mesh_id === mesh.id)
  // and candidates (no mesh at all).
  const { data: orgLocations = [] } = useQuery({
    queryKey: ['locations', { organization_id: mesh.organization_id }],
    queryFn: () => locationsApi.getAll({ organization_id: mesh.organization_id }),
    enabled: open,
  });

  const members = orgLocations
    .filter((l) => l.mesh_id === mesh.id)
    .sort((a, b) => a.name.localeCompare(b.name));
  // Locations already in a different mesh are deliberately not offered —
  // moving one is an explicit act on the location's own edit form.
  const candidates = orgLocations
    .filter((l) => !l.mesh_id)
    .sort((a, b) => a.name.localeCompare(b.name));

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['meshes'] });
    queryClient.invalidateQueries({ queryKey: ['locations'] });
  };

  const addMutation = useMutation({
    mutationFn: (location: Location) =>
      locationsApi.update(location.id, { mesh_id: mesh.id }),
    onSuccess: () => {
      invalidate();
      setSelectedLocation(null);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(extractApiError(err, 'Failed to add location to mesh'));
    },
  });

  const removeMutation = useMutation({
    mutationFn: (location: Location) =>
      locationsApi.update(location.id, { mesh_id: null }),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (err: unknown) => {
      setError(extractApiError(err, 'Failed to remove location from mesh'));
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Members of {mesh.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Organization: {mesh.organization?.name}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Add member form */}
        <Stack direction="row" spacing={1} sx={{ mb: 0.5 }} alignItems="center">
          <Autocomplete
            value={selectedLocation}
            onChange={(_event, newValue: Location | null) => setSelectedLocation(newValue)}
            options={candidates}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Add Location" size="small" />}
            sx={{ flex: 1 }}
            size="small"
          />
          <Button
            variant="contained"
            size="small"
            onClick={() => selectedLocation && addMutation.mutate(selectedLocation)}
            disabled={!selectedLocation || addMutation.isPending}
          >
            Add
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Only locations not already in a mesh are listed. To move a location between
          meshes, edit the location itself.
        </Typography>

        {/* Member list */}
        <List dense>
          {members.map((location) => (
            <ListItem key={location.id}>
              <ListItemText
                primary={location.name}
                secondary={location.slug}
              />
              <ListItemSecondaryAction>
                {!location.active && (
                  <Chip label="Inactive" size="small" variant="outlined" sx={{ mr: 1, color: 'text.disabled' }} />
                )}
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={`Remove ${location.name}`}
                  onClick={() => removeMutation.mutate(location)}
                  disabled={removeMutation.isPending}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {members.length === 0 && (
            <ListItem>
              <ListItemText
                primary="No members yet"
                secondary="Add locations to this mesh using the form above"
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
